import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Truck,
  ClipboardCheck,
  Banknote,
  ShieldCheck,
  Clock,
  Recycle,
} from "lucide-react";
import Header from "@/components/Header";
import HeroTruckPhoto from "@/components/HeroTruckPhoto";

const LOGO_URL = "/logo.jpg";
const HERO_IMG = "/hero.png";
const SERVICE_IMG = "/why-us.png";

const PHONE_DISPLAY = "(289) 300-4040";
const PHONE_TEL = "+12893004040";
const PHONE2_DISPLAY = "(647) 676-9384";
const PHONE2_TEL = "+16476769384";
const SERVICE_AREA = "Ontario, Canada";
const ADDRESS = "2 Racine Road, Etobicoke, ON M9W 2Z1";
const ADDRESS_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent("2 Racine Road, Etobicoke, ON M9W 2Z1");
const CONTACT_EMAIL = "mrcrusherscrap@gmail.com";

const STARTER_FIELDS = [
  { name: "name", label: "Full name", placeholder: "Jane Doe", type: "text" },
  { name: "phone", label: "Phone", placeholder: "(289) 555-0100", type: "tel" },
  { name: "email", label: "Email (optional)", placeholder: "you@example.com", type: "email" },
];

export default function Landing() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("in");
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="bg-white text-slate-900" data-testid="landing-page">
      <Header />

      {/* HERO — Torqi-style centered vertical stack on blue gradient */}
      <section
        id="hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#0055FF] via-[#0044DD] to-[#003ECE]"
      >
        {/* Subtle white grid texture */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Soft radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.18), transparent 60%)",
          }}
        />

        <div className="relative max-w-5xl mx-auto px-6 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          {/* Live pill */}
          <div className="reveal inline-flex items-center gap-2 bg-white/95 backdrop-blur px-4 py-1.5 rounded-full text-xs font-bold text-[#0055FF] uppercase tracking-widest shadow">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Cash for scrap cars · Same-day pickup · {SERVICE_AREA}
          </div>

          {/* Headline */}
          <h1
            className="reveal mt-6 font-display font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white max-w-4xl mx-auto"
            data-testid="hero-headline"
          >
            Sell Your Scrap Car{" "}
            <span className="text-[#FDE68A]">Fast</span> with MR Crusher.
          </h1>

          {/* Subtext */}
          <p className="reveal mt-6 max-w-2xl mx-auto text-base sm:text-lg text-white/85 leading-relaxed">
            We buy old, junk, salvaged, and end-of-life vehicles in any
            condition — no title, no problem. Get a guaranteed quote in minutes
            and free pickup the same day across {SERVICE_AREA}.
          </p>

          {/* CTAs */}
          <div className="reveal mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link
              to="/"
              data-testid="hero-primary-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-[#0055FF] px-8 py-4 font-semibold rounded-full transition-colors group shadow-lg"
            >
              Get Instant Offer
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border-2 border-white/40 hover:border-white/70 text-white px-8 py-4 font-medium rounded-full transition-all"
            >
              <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
            </a>
          </div>

          {/* Truck illustration — centered */}
          <div
            className="reveal mt-14 md:mt-16 max-w-3xl mx-auto"
            data-testid="hero-illustration-frame"
          >
            <HeroTruckPhoto />
          </div>

          {/* Trust badges — centered */}
          <div className="reveal mt-12 md:mt-16 grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
            <TrustStat value="12k+" label="Cars crushed" />
            <TrustStat value="60min" label="Avg. pickup" />
            <TrustStat value="$$$" label="Top dollar" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="reveal max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF] mb-4">
              How it works
            </div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">
              Three steps. No hassle. Cash in hand.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-slate-200">
            <Step
              num="01"
              icon={<ClipboardCheck className="h-6 w-6" />}
              title="Tell us about the car"
              copy="Submit a few details — make, year, condition, location. Our team reads every message instantly."
            />
            <Step
              num="02"
              icon={<Banknote className="h-6 w-6" />}
              title="Get an instant offer"
              copy="We send you a guaranteed cash quote within minutes. No haggling, no lowballs, no fine print."
            />
            <Step
              num="03"
              icon={<Truck className="h-6 w-6" />}
              title="We tow it. You get paid."
              copy="Free same-day pickup anywhere across Ontario. We hand you cash on the spot when the tow truck arrives."
            />
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="border-b border-slate-200 bg-[#F4F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <img
                src={SERVICE_IMG}
                alt="MR Crusher tow truck loading a scrap car at the yard"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF] mb-4">
              Why MR Crusher
            </div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">
              Built for speed. Priced for fairness.
            </h2>
            <p className="mt-6 text-slate-600 max-w-xl">
              We've been crushing it since day one. Family-run, fully licensed
              recyclers serving {SERVICE_AREA} who pay top dollar for your
              end-of-life vehicle — no matter the condition.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <Bullet
                icon={<ShieldCheck className="h-5 w-5" />}
                title="Licensed & insured"
                copy="Fully bonded, EPA-registered, and provincially compliant scrap operation."
              />
              <Bullet
                icon={<Clock className="h-5 w-5" />}
                title="Same-day pickup"
                copy="Most pickups happen within 60 minutes of accepted offer."
              />
              <Bullet
                icon={<Banknote className="h-5 w-5" />}
                title="Cash on the spot"
                copy="No checks, no waiting. We pay drivers cash at pickup."
              />
              <Bullet
                icon={<Recycle className="h-5 w-5" />}
                title="Eco recycling"
                copy="Up to 95% of every vehicle is recycled responsibly."
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / WIZARD */}
      <section id="contact" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF] mb-4">
              Get an offer
            </div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">
              Tell us about your car.
              <br />
              We'll quote it in minutes.
            </h2>
            <p className="mt-6 text-slate-600">
              Answer a few quick questions and we'll text or call you back with
              a guaranteed cash offer. Every request lands directly in our
              crusher dashboard.
            </p>
            <div className="mt-10 space-y-4 text-sm text-slate-700">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#0055FF]" />
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-email"
                  className="hover:text-[#0055FF] transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#0055FF]" />
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="contact-phone-display"
                  className="hover:text-[#0055FF] transition-colors"
                >
                  {PHONE_DISPLAY}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#0055FF]" />
                <a
                  href={`tel:${PHONE2_TEL}`}
                  data-testid="contact-phone2-display"
                  className="hover:text-[#0055FF] transition-colors"
                >
                  {PHONE2_DISPLAY}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#0055FF] mt-0.5 shrink-0" />
                <a
                  href={ADDRESS_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address"
                  className="hover:text-[#0055FF] underline-offset-4 hover:underline transition-colors"
                >
                  {ADDRESS}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <StarterForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 grid place-items-center shrink-0">
              <img
                src={LOGO_URL}
                alt="MR Crusher logo"
                className="h-full w-full object-contain"
              />
            </div>
            <span className="font-display font-bold text-2xl uppercase tracking-tight">
              MR Crusher
            </span>
          </div>
          <div className="text-xs text-slate-500 font-mono-plex">
            © {new Date().getFullYear()} MR Crusher Scrap Co. — Licensed
            recycler · {SERVICE_AREA}
          </div>
        </div>
      </footer>
    </div>
  );
}


/* ---------- small bits ---------- */
function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-semibold text-2xl text-slate-900">
        {value}
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function TrustStat({ value, label }) {
  return (
    <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl px-3 py-4 sm:px-6 sm:py-5 shadow-lg">
      <div className="font-display font-semibold text-2xl sm:text-3xl text-white">
        {value}
      </div>
      <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/70 mt-1">
        {label}
      </div>
    </div>
  );
}

function Step({ num, icon, title, copy }) {
  return (
    <div
      className="bg-white p-8 md:p-10 hover:bg-slate-50 transition-colors group"
      data-testid={`how-step-${num}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-xs text-slate-400">{num}</span>
        <div className="h-10 w-10 grid place-items-center bg-slate-100 group-hover:bg-[#0055FF] group-hover:text-white transition-colors">
          {icon}
        </div>
      </div>
      <h3 className="mt-8 font-display font-semibold text-xl tracking-tight">
        {title}
      </h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{copy}</p>
    </div>
  );
}

function Bullet({ icon, title, copy }) {
  return (
    <div className="flex gap-4">
      <div className="h-10 w-10 shrink-0 grid place-items-center bg-white border border-slate-200 text-[#0055FF]">
        {icon}
      </div>
      <div>
        <div className="font-display font-semibold text-base">{title}</div>
        <div className="mt-1 text-sm text-slate-600">{copy}</div>
      </div>
    </div>
  );
}


/* ---------- STARTER FORM ---------- */
// Shows only the first step. The moment the seller types anything, we hand off
// to /quote with whatever they've started typing — they continue the rest there.
function StarterForm() {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);

  const handoff = (initial) => {
    if (started) return;
    setStarted(true);
    navigate("/", { state: { initial } });
  };

  return (
    <div
      className="bg-white border border-slate-200 shadow-[0_20px_60px_-15px_rgba(0,85,255,0.18)] p-8 md:p-10 rounded-3xl"
      data-testid="starter-form"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
          Step 1 of 13
        </span>
        <span className="text-xs font-mono-plex text-slate-500">~2 min</span>
      </div>

      <h3
        className="mt-4 font-display font-semibold text-2xl sm:text-3xl tracking-tight"
        data-testid="starter-title"
      >
        Let's start with your contact details
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        We'll text or call back with a cash offer in minutes.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        {STARTER_FIELDS.map((f, idx) => (
          <div
            key={f.name}
            className={f.name === "email" ? "sm:col-span-2" : ""}
          >
            <label className="text-xs font-medium uppercase tracking-widest text-slate-500">
              {f.label}
            </label>
            <input
              type={f.type}
              placeholder={f.placeholder}
              data-testid={`starter-input-${f.name}`}
              onChange={(e) =>
                handoff({ [f.name]: e.target.value })
              }
              onFocus={(e) => {
                // No-op: redirect only on actual input change
                void e;
              }}
              className="mt-2 w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg"
              autoComplete={f.name === "email" ? "email" : f.name === "phone" ? "tel" : "name"}
              tabIndex={idx + 1}
            />
          </div>
        ))}
      </div>

      <Link
        to="/"
        data-testid="starter-cta"
        className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] text-white px-8 py-4 font-medium rounded-full transition-colors group"
      >
        Continue
        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Link>

      <p className="mt-4 text-xs text-slate-500 text-center">
        12 quick questions about your car — takes about 2 minutes.
      </p>
    </div>
  );
}
